<template>
  <div>
    <div id="preview">

      <cover-page
        :project-info="projectInfo"
        :product-brand="productBrand">
      </cover-page>

      <!--////////////////////////////
      ///// Contact Information //////
      ////////////////////////////////-->

      <section 
        class="contact-info" 
        data-type="contact-info" 
        id="contact-info"
      >
        <img
          class="running-logo"
          :src="headerLogo"/>

        <div class="contact-info__outline">
          <table class="contact-info__table">
            <tr v-for="(info, label) in projectInfo.contact.value"
              :key="label">
              <td>{{ label }}</td>
              <td>{{ info }}</td>
            </tr>
          </table>
          <table class="contact-info__table">
            <tr v-for="(info, label) in projectInfo.sales_person.value"
              :key="label">
              <td>{{ label }}</td>
              <td>{{ info }}</td>
            </tr>
          </table>
        </div>

        <div
          class="running-footer"
          :class="productBrand.toLowerCase()">
          <div class="running-footer__top">
          </div>
          <div class="running-footer__bottom">
          </div>
        </div>
      </section>

      <!--////////////////////////////
      ///// Auto-generated TOC //////
      ////////////////////////////////-->

      <section data-type="toc" id="toc">

        <div>
          We are pleased to supply you with our quotation proposal as follows:
        </div>
        <div id="toc-content" class="toc">
        </div>

      </section>

      <!--////////////////////////////
      ///// General Information //////
      ////////////////////////////////-->

      <general-information 
        :proposal="proposal" 
        :product-brand="productBrand"
        :replacements="replacements" 
      />

      <!--////////////////////////////
      /// section loops begin here ///
      ////////////////////////////////-->

      <template
        v-for="(section, i) in proposalContent">

        <section
          class="content-section"
          v-if="section.type != 'optional' && 
            (!Object.keys(section).includes('visible') || section.visible)"
          :key="section.name + '-' + i">

          <h1>{{ section.name }}</h1>
          <hr class="h1-underline"/>

          <!-- static sections -->
          <template v-if="section.type == 'static'">

            <template 
              v-for="(subsection, i) in section.subsections">

              <div
                class="content-subsection"
                v-bind="subsection.d_bind"
                v-if="subsection.value.length > 0 && 
                  Array.isArray(subsection.value) ?
                  subsection.value.filter(d => 
                    !Object.keys(d).includes('visible') || d.visible == true).length > 0
                  : !!subsection.value && subsection.value != ''"
                :key="subsection + '-' + i">

                <h2>{{ subsection.label }}</h2>

                <template v-if="subsection.d_component == 'VList'">
                  <div class="content">
                    <ul>
                      <li
                        v-for="value in subsection.value"
                        :key="value">
                        {{ value }}
                      </li>
                    </ul>
                  </div>
                </template>

                <template v-if="subsection.d_component == 'VSubsection'">

                  <div
                    class="content-sub-subsection"
                    v-for="(subSubsection, i) in subsection.value"
                    :key="i">

                    <template v-if="subSubsection.visible || 
                      !Object.keys(subSubsection).includes('visible')">

                      <template v-for="(template, i) in subsection.template">

                        <component
                          :key="template.label + '-' + i"
                          :is="template.tag"
                          v-if="template.tag != 'img'">
                          {{ subSubsection[template.label] }}
                        </component>

                        <img
                          v-if="template.tag == 'img' && subSubsection[template.label].url"
                          :key="template.label + '-' + i"
                          :class="template.display_class"
                          :src="subSubsection[template.label].url"/>

                      </template>
                    
                    </template>

                  </div>

                  <signature-line 
                    class="mt-5"
                    v-if="subsection.id == 'drawing_highlight'"
                    :section-name="subsection.label"
                    :has-name="false"
                    :has-signature="false"
                    :has-initial="true"/>
                </template>

                <template v-if="subsection.d_component == 'DynamicMaterials'">
                  <div class="break-after">
                    <dynamic-materials
                      :template="subsection.template" 
                      :materials="subsection.value"/>
                    <small>**Use as reference only. Colors, patterns, and shades to be approved during the sample approval phase.</small>
                  </div>
                </template>

                <template v-if="subsection.d_component == 'TextAndImageD'">
                  <div class="content">
                    <p>{{ subsection.value.text }}</p>
                    <img :src="subsection.value.image.url"/>
                  </div>
                </template>

                <template v-if="subsection.d_component == 'VRichTextRenderer'">
                  <v-rich-text-renderer 
                    :value="subsection.value" 
                    :replacements="replacements" 
                  />
                </template>

              </div>
            </template>

            <div 
              v-if="section.id == 'general_tos' && section.visible"
              class="mt-6">
              <div>{{ productBrand }} Signature:</div>
              <signature-line 
                v-if="section.id == 'general_tos'"
                class="mt-2 mb-5"
                :section-name="section.name"
                has-date
              />
              <div>Client Signature:</div>
              <signature-line 
                v-if="section.id == 'general_tos'"
                class="mt-2"
                :section-name="section.name"
                has-date
              />
            </div> 

          </template>
          <!-- static sections end -->

          <!-- dynamic sections -->
          <template v-if="section.type == 'dynamicSubsections'">

            <div
              class="content-subsection"
              v-for="(subsection, i) in section.subsections"
              :key="i">

              <template 
                v-for="(template, i) in section.subsection_template">

                <component
                  :key="template.label + '-' + i"
                  :is="template.tag"
                  v-if="template.tag != 'img'"
                >
                  {{ subsection[template.label] }}
                </component>

                <img
                  v-if="template.tag == 'img' && subsection[template.label].url"
                  :key="template.label + '-' + i"
                  :class="template.display_class"
                  :src="subsection[template.label].url"/>

              </template>

            </div>

          </template>
          <!-- dynamic sections end -->

          <template v-if="section.type == 'pricing'">

            <div
              class="content-subsection"
              :class="{ 'break-after': section.id == 'pricing_summary' }"
              v-for="(subsection, i) in section.subsections"
              :key="i">

              <div>
                <template v-for="(el, elIndex) in subsection">
                  <component
                    v-if="(el.display.component == 'h2' && 
                      el.value != '' && el.value != null) || 
                      el.display.component != 'h2'"
                    :key="`pricing-sub-el-${elIndex}`"
                    :is="el.display.component"
                    :estimation="estimation"
                    v-bind="el.display.bind"
                    :value="el.value"
                    :replacements="replacements"
                  >
                    {{ el.value }}
                  </component>
                </template>
              </div>

              <signature-line 
                v-if="section.id == 'pricing_summary' || 
                  (section.id == 'detailed_pricing' && (i + 1) == section.subsections.length)"
                :class="section.id == 'pricing_summary' ? 'mt-4' : 'mt-5'"
                :section-name="`${productBrand == 'Mitrex' ? 
                  filters.Uppercase(subsection.filter(
                    d => d.id == 'content')[0].content_id) + ' ' + section.name : 
                  section.name}`"
                :has-name="false"
                :has-signature="false"
                :has-initial="true"/>

            </div>

          </template>

        </section>

        <component
          class="content-section"
          v-if="section.type == 'optional' && section.visible == true"
          :key="section.name + '-' + i"
          :is="section.component"
          v-bind="section.bind"
          :rfq="rfq"
          :has-mpa-price="proposal.project_info.pricing ? proposal.project_info.pricing.has_mpa_price : false"
          :has-dpa-price="proposal.project_info.pricing ? proposal.project_info.pricing.has_dpa_price : false"
          :phone="projectInfo.sales_person.value.Phone"
          :email="projectInfo.sales_person.value.Email">
        </component>

      </template>

    </div>

    <div id="preview-output" :class="productBrand.toLowerCase()">
    </div>

  </div>
</template>

<script>
import VRichTextRenderer from '@/components/base/VRichTextRenderer'
import GeneralInformation from './GeneralInformation'
import PricingSummary from './PricingSummary'
import ValueSolarWall from './ValueSolarWall'
import ValueSolarRailing from './ValueSolarRailing'
import DetailedPricingRailing from './DetailedPricingRailing' 
import DetailedPricingWall from './DetailedPricingWall'
import DetailedPricingCladding from './DetailedPricingCladding'
import DetailedPricingCladifyItem from './DetailedPricingCladifyItem'
import DetailedPricingCladifyService from './DetailedPricingCladifyService'
import PricingSummaryCladify from './PricingSummaryCladify'

import DynamicMaterials from './DynamicMaterials'
import RetrofitSavings from './RetrofitSavings'
import TaxIncentivesPage from './TaxIncentivesPage'
import TaxIncentivesCostBreakdown from './TaxIncentivesCostBreakdown'
import TaxIncentivesSummary from './TaxIncentivesSummary'
import CoverPage from './CoverPage'
import AboutCladify from './AboutCladify'
import BatteryAndInverter from './BatteryAndInverter'
import EnvironmentalImpact from './EnvironmentalImpact'
import ValueOfMitrex from './ValueOfMitrex'
import SampleSwatchesMitrex from './SampleSwatchesMitrex'
import PastProjectsOne from './PastProjectsOne'
import PastProjectsTwo from './PastProjectsTwo'
import PastProjectsThree from './PastProjectsThree.vue'
import GeneralTermsOfSale from './GeneralTermsOfSale'
import LastPage from './LastPage'

import SignatureLine from '@/components/proposalmaker/SignatureLine'

import { Previewer, registerHandlers, Handler } from "pagedjs"
import createToc from '@/helpers/toc.js'
import pdfStyle from '@/helpers/pdfStyle'

import { format, parseJSON } from 'date-fns'
import filters from '@/filters'

export default {
  props: {
    proposal: {
      type: Object,
      required: true
    },
    rfq: {
      type: Object
    },
    installationMethods: {
      type: Array
    },
    productBrand: {
      type: String,
      required: true
    }
  },
  components: {
    VRichTextRenderer, GeneralInformation, PricingSummary, ValueSolarWall, 
    ValueSolarRailing, DetailedPricingRailing, DetailedPricingWall, 
    DetailedPricingCladding, DetailedPricingCladifyItem, 
    DetailedPricingCladifyService, PricingSummaryCladify, CoverPage, 
    AboutCladify, BatteryAndInverter, EnvironmentalImpact, ValueOfMitrex,
    PastProjectsOne, PastProjectsTwo, PastProjectsThree, SampleSwatchesMitrex,
    GeneralTermsOfSale, LastPage, TaxIncentivesPage, TaxIncentivesCostBreakdown, 
    TaxIncentivesSummary, RetrofitSavings,
    DynamicMaterials, SignatureLine
  },
  data() {
    return {
      formatDate: (date) => {
        return date ? format(date, "MM/dd/yyyy") : null;
      },
      parseDate: (date) => { return date ? parseJSON(date) : date },
      filters: filters
    }
  },
  computed: {
    projectInfo() {
      return this.proposal.project_info;
    },
    proposalContent() {
      return this.proposal.proposal_content;
    },
    headerLogo() {
      return this.productBrand == 'Mitrex' ? 
        require('@/assets/mitrex_logo_sm.png') : 
        require('@/assets/cladify_logo_sm.png');
    },
    estimation({ rfq }) {
      return rfq.c_estimation ? rfq.c_estimation : rfq.estimation;
    },
    replacements() {
      const res = [];
      const { rfq, installationMethods, productBrand } = this;
      const installPmtInstruction = [
        '{PAYMENT_INSTRUCTION_INSTALL}', 
        `<li>${productBrand} Material Payment Instruction; 30% deposit, ` + 
        '20% shop drawings and raw material ordering, 50% upon completion ' +
        'of production and installation payment instruction; 10% on mobilization, ' +
        'and 90% as per progress.  Detail payment instructions are to be '+ 
        'submitted at the time of the contract.</li>'
      ]

      if (rfq && rfq.c_estimation && this.proposal.project_info.pricing.rfq_id) {
        if (productBrand === 'Mitrex') {
          // Solar Cladding
          const solarCladding = rfq.c_estimation.estimation_summary.summary
            .filter(item => item.pricing_summary.includes('solar_wall'))
            .map(item => item.measure)
            .reduce((a, b) => +a + +b, 0)
          res.push(['{TOTAL_SOLAR_CLADDING_SQFT}', this.$options.filters.commasep(solarCladding, 0)]);

          // Fabricated Solar Cladding
          const irregularSolar = rfq.c_estimation.estimation_summary.irregular_price_by_material
            .filter(item => item.product_brand == 'Mitrex')
            .map(item => item.sum_linear_ft)
            .reduce((a, b) => +a + +b, 0)
          res.push(['{TOTAL_SOLAR_CLADDING_LFT}', this.$options.filters.commasep(irregularSolar, 0)]);

          // Solar Railing
          var railingLinearFt;
          Object.keys(rfq.c_estimation.estimation_summary).forEach(key => {
            if (key.includes('solar_railing_')) {
              railingLinearFt = rfq.c_estimation.estimation_summary[key].map(
                d => d.sum_linear_ft
              ).reduce((a, b) => a + +b, 0);
            }
          })
          res.push(['{TOTAL_RAILING_LFT}', this.$options.filters.commasep(railingLinearFt, 0)]);

          // Regular Cladding
          const cladding = rfq.c_estimation.estimation_summary.summary.filter(
            d => d.pricing_summary == 'cladding'
          )[0];
          if (cladding) {
            res.push(['{TOTAL_CLADDING_SQFT}', this.$options.filters.commasep(cladding.measure, 0)]);
          }

          // Fabricated Regular Cladding
          const irregularCladding = rfq.c_estimation.estimation_summary.irregular_price_by_material.filter(
            d => d.product_brand == 'Cladify'
          ).reduce((a,b) => a += b.measure, 0);
          res.push(['{TOTAL_CLADDING_LFT}', this.$options.filters.commasep(irregularCladding, 0)])

          // MPA and DPA pricing
          const totalMpaPrice = rfq.c_estimation.estimation_summary.summary.map(item => item.price).reduce((a, b) => +a + +b, 0);
          const totalDpaPrice = rfq.c_estimation.estimation_summary_dpa.summary.map(item => item.price).reduce((a, b) => +a + +b, 0);
          if (totalMpaPrice) {
            res.push(['{PRICE_BASE_MPA}', this.$options.filters.currency(totalMpaPrice)])
          }
          if (totalDpaPrice) {
            res.push(['{PRICE_BASE_DPA}', this.$options.filters.currency(totalDpaPrice)])
          }

          // Products
          let products = ''
          if (solarCladding > 0) {
            products += 'Mitrex Solar Cladding with Honeycomb backing technology'
          }
          if (railingLinearFt > 0) {
            if (solarCladding > 0) {
              products += ' and'
            }
            products += ' Mitrex Solar Railing'
          }
          res.push(['{PRODUCTS_TEXT}', products])
        } else {
          res.push(['{TOTAL_CLADDING_SQFT}', this.$options.filters.commasep(rfq.c_estimation.total_area_sqft, 0)]);
          res.push(['{TOTAL_CLADDING_LFT}', this.$options.filters.commasep(rfq.c_estimation.total_linear_ft, 0)]);

          res.push(['{PRICE_BASE}', this.$options.filters.currency(rfq.c_estimation.total_price_pre_tax)])
        }

        let methodsList = ''
        installationMethods.forEach(method => {
          methodsList += `<li>${method.installation_name}</li>`
        })
        res.push(['{INSTALLATION_METHODS_LIST}', methodsList]);

        let methodsText = ''
        const noMethods = installationMethods.length;
        installationMethods.forEach((method, i) => {
          methodsText += `${noMethods > 1 ? i+1 === noMethods ? 'and' : ', ' : ' '}${method.installation_name}`
        })
        res.push(['{INSTALLATION_METHODS_TEXT}', methodsText]);

        let pmf = ''
        rfq.c_estimation.items.forEach(item => {
          let itemStr = ''
          const facing = item.materials.find(material => material.product_master.product_category_id === 13);
          const backing = item.materials.find(material => material.product_master.product_category_id === 14);
          const railing = item.materials.find(material => material.product_master.product_category_id === 20);
          const installation = item.installations.find(install => install.installation.installation_type_id === 1);
          
          if (facing) {
            itemStr += `<li>${facing.product_master.name}</li>`
          }
          if (backing) {
            itemStr += `<li>${backing.product_master.name} backing technology</li>`
          }
          if (railing) {
            itemStr += `<li>${railing.product_master.name}</li>`
          }
          if (installation) {
            itemStr += `<li>${facing ? 'Rainscreen Cladding System - ' : ''}${installation.installation.installation_name}</li>`
          }
          if (item.processing.length > 0) {
            item.processing.forEach(processing => {
              itemStr += `<li>${processing.processing.processing_name}</li>`
            })
          }
          if (facing && facing.product_master.product_brand === 'Mitrex') {
            itemStr += '<li>Wiring and connections between panels</li>'
          }
          if (railing && railing.product_master.product_brand === 'Mitrex') {
            itemStr += '<li>Mounting and fastening accessories</li>'
            itemStr += '<li>Electrical wiring and connections</li>'
          }

          itemStr = `<li>${facing && facing.product_master.product_brand === 'Mitrex' ? 'Mitrex Solar ' : ''}
            ${item.architecture_name}<ul>${itemStr}</ul></li>`
          pmf += itemStr
        })

        pmf = `<ul>${pmf}</ul>`;

        res.push(['{PMF}', pmf]);

        if (rfq.has_install) {
          res.push(installPmtInstruction);
        } else {
          res.push(['{PAYMENT_INSTRUCTION_INSTALL}', '']);
        }
      } else {
        const supplyInstall = 
          this.proposal.proposal_info.find(section => section.id === 'supply_install');
        if (supplyInstall && supplyInstall.value.includes('install')) {
          res.push(installPmtInstruction);
        } else {
          res.push(['{PAYMENT_INSTRUCTION_INSTALL}', '']);
        }
      }

      return res
    }
  },
  mounted() {
    this.preview();
  },
  methods: {
    preview() {
      class handlers extends Handler {
        constructor(chunker, polisher, caller) {
          super(chunker, polisher, caller);
        }

        beforeParsed(content){
          createToc({
            content: content,
            tocElement: '#toc-content',
            titleElements: ['h1', 'h2']
          });
        }
      }
      registerHandlers(handlers);
      
      const previewer = new Previewer();

      var fragment = new DocumentFragment();
      let previewEls = [].slice.call(document.querySelector("#preview").childNodes);

      previewEls.forEach(child => {
        fragment.appendChild(child);
      })

      previewer
        .preview(
          fragment,
          [{"inline": pdfStyle}],
          document.querySelector("#preview-output")
        )
        .then(flow => {
          // if (this.$route.hash) {
          //   let el = document.querySelector(this.$route.hash);
          //   if (el) {
          //     el.scrollIntoView({ behavior: 'smooth' });
          //   }
          // }
          console.log("preview rendered, total pages", flow.total, { flow });
        });
    }
  }
}
</script>

<style scoped>
@import '../../styles/pdf.css';
@import '../../styles/interface.css';
</style>
